import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import useApi from "../../../CustomHooks/useApi";

const ROLE = {
  MASTER_DISTRIBUTOR: { label: "Master Distributor", head: "bg-violet-600",  badge: "bg-violet-100 text-violet-700",  row: "hover:bg-violet-50/40" },
  DISTRIBUTOR:        { label: "Distributor",         head: "bg-blue-600",    badge: "bg-blue-100 text-blue-700",      row: "hover:bg-blue-50/40"   },
  MERCHANT:           { label: "Merchant",            head: "bg-emerald-600", badge: "bg-emerald-100 text-emerald-700",row: "hover:bg-emerald-50/40"},
};

const ServiceRateListCard = ({ serviceType, refreshKey, onEditSlab, onBulkEdit, onDeleteRate }) => {
  const [loading,       setLoading]       = useState(false);
  const [fetchError,    setFetchError]    = useState("");
  const [data,          setData]          = useState(null);
  const [searchTerm,    setSearchTerm]    = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [openSections,  setOpenSections]  = useState({ MASTER_DISTRIBUTOR: true, DISTRIBUTOR: true, MERCHANT: true });
  const [popupUser,     setPopupUser]     = useState(null);
  const [deletingSlabId,setDeletingSlabId]= useState(null);
  const [confirmSlab,   setConfirmSlab]   = useState(null);

  const token = useSelector((s) => s.auth.employeeDetails?.token);
  const authH = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const { callApi: getRates, error: apiError } = useApi(`/commission/rates/view/service/${serviceType}`, "GET");

  const fetchRates = async () => {
    try {
      setLoading(true); setFetchError("");
      const res = await getRates();
      if (res == null) { setFetchError(apiError || "Failed to load rates."); setData(null); }
      else setData(res);
    } catch (err) { setFetchError(err?.message || "Unexpected error."); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (serviceType) { setSelectedUsers([]); setSearchTerm(""); fetchRates(); }
  }, [serviceType, refreshKey]);

  const handleDeleteSlab = async (slab) => {
    setConfirmSlab(null);
    setDeletingSlabId(slab.id);
    const base = process.env.REACT_APP_API_BASE_URL;
    try {
      await axios.delete(`${base}/commission/rates/service/${serviceType}/user-type/${popupUser.userType}/user/${popupUser.userId}`, { headers: authH });
      const remaining = popupUser.slabs.filter((s) => s.id !== slab.id);
      if (remaining.length > 0) {
        await axios.post(`${base}/commission/rates/set`, {
          serviceType, userType: popupUser.userType, userId: popupUser.userId,
          rateTiers: remaining.map((s) => ({
            minAmount: s.minAmount, maxAmount: s.maxAmount,
            agentChargeType: s.agentChargeType, agentChargeValue: s.agentChargeValue,
            agentCommType: s.agentCommType,     agentCommValue: s.agentCommValue,
            distCommType:  s.distCommType,      distCommValue:  s.distCommValue,
            mdCommType:    s.mdCommType,         mdCommValue:    s.mdCommValue,
            merchantTdsPercentage: s.merchantTdsPercentage,
            distTdsPercentage: s.distTdsPercentage, mdTdsPercentage: s.mdTdsPercentage,
          })),
        }, { headers: authH });
      }
      const updated = popupUser.slabs.filter((s) => s.id !== slab.id);
      if (updated.length === 0) setPopupUser(null);
      else setPopupUser((p) => ({ ...p, slabs: updated }));
      fetchRates();
    } catch (err) {
      setFetchError(err?.response?.data?.message || "Failed to delete slab.");
    } finally { setDeletingSlabId(null); }
  };

  const buildUserList = (rates, roleType) => {
    const grouped = {};
    (rates || []).forEach((item) => {
      const uid = item.userId || item.username;
      if (!uid) return;
      if (!grouped[uid]) grouped[uid] = { userId: uid, userType: (typeof item.userType === "string" ? item.userType : roleType) || roleType, shopName: item.shopName, fullName: item.fullName, mobileNumber: item.mobileNumber, slabs: [] };
      grouped[uid].slabs.push(item);
    });
    return Object.values(grouped);
  };

  const allUsers = [
    ...buildUserList(data?.masterDistributorRates, "MASTER_DISTRIBUTOR"),
    ...buildUserList(data?.distributorRates, "DISTRIBUTOR"),
    ...buildUserList(data?.merchantRates, "MERCHANT"),
  ];

  const filtered = allUsers.filter((u) => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return u.userId?.toLowerCase().includes(s) || u.fullName?.toLowerCase().includes(s) || u.mobileNumber?.toLowerCase().includes(s);
  });

  const byType = (t) => filtered.filter((u) => u.userType === t);

  const toggleUser = (user) => {
    const exists = selectedUsers.find((u) => u.userId === user.userId);
    setSelectedUsers(exists ? selectedUsers.filter((u) => u.userId !== user.userId) : [...selectedUsers, user]);
  };

  const renderSection = (type, users) => {
    const meta = ROLE[type];
    const open = openSections[type];
    const allSel = users.length > 0 && users.every((u) => selectedUsers.some((s) => s.userId === u.userId));

    return (
      <div key={type} className="rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
        {/* Section header */}
        <button
          onClick={() => setOpenSections((p) => ({ ...p, [type]: !p[type] }))}
          className={`w-full flex items-center justify-between px-5 py-3.5 text-left ${meta.head} text-white`}
        >
          <div className="flex items-center gap-3">
            <span className="font-bold text-sm">{meta.label}</span>
            <span className="bg-white/20 text-white px-2.5 py-0.5 rounded-full text-xs font-bold">
              {users.length} user{users.length !== 1 ? "s" : ""}
            </span>
          </div>
          <span className="text-white/70 text-lg font-bold">{open ? "−" : "+"}</span>
        </button>

        {open && (
          <div className="bg-white overflow-x-auto">
            {users.length === 0 ? (
              <div className="py-10 text-center text-gray-400 text-sm">No rates configured for this service</div>
            ) : (
              <table className="w-full min-w-[700px] text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="px-4 py-3 text-left w-10">
                      <input type="checkbox" checked={allSel}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedUsers((p) => [...p, ...users.filter((u) => !p.some((s) => s.userId === u.userId))]);
                          else setSelectedUsers((p) => p.filter((s) => !users.some((u) => u.userId === s.userId)));
                        }}
                        className="w-4 h-4 accent-[#0A2A4F] rounded"
                      />
                    </th>
                    {["User ID", "Full Name", "Shop", "Mobile", "Slabs", "Actions"].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-400 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.userId} className={`border-b border-gray-50 transition ${meta.row}`}>
                      <td className="px-4 py-3.5">
                        <input type="checkbox" checked={selectedUsers.some((s) => s.userId === user.userId)}
                          onChange={() => toggleUser(user)} className="w-4 h-4 accent-[#0A2A4F] rounded" />
                      </td>
                      <td className="px-4 py-3.5 font-bold text-gray-800">{user.userId}</td>
                      <td className="px-4 py-3.5 text-gray-500">{user.fullName || "—"}</td>
                      <td className="px-4 py-3.5 text-gray-500">{user.shopName || "—"}</td>
                      <td className="px-4 py-3.5 text-gray-500">{user.mobileNumber || "—"}</td>
                      <td className="px-4 py-3.5">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${meta.badge}`}>
                          {user.slabs.length} slab{user.slabs.length !== 1 ? "s" : ""}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex gap-2">
                          <button onClick={() => setPopupUser(user)}
                            className="px-3 py-1.5 rounded-lg bg-[#0A2A4F] text-white text-xs font-bold hover:bg-[#0d3562] transition">
                            View Slabs
                          </button>
                          <button onClick={() => onDeleteRate({ userId: user.userId, userType: user.userType })}
                            className="px-3 py-1.5 rounded-lg bg-red-50 text-red-600 text-xs font-bold hover:bg-red-100 transition">
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-14 text-center">
      <div className="w-8 h-8 border-2 border-[#0A2A4F] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
      <p className="text-sm text-gray-400">Loading rates for {serviceType}…</p>
    </div>
  );

  if (fetchError) return (
    <div className="bg-white rounded-2xl border border-red-200 shadow-sm p-12 text-center">
      <p className="text-red-500 text-sm font-medium mb-4">{fetchError}</p>
      <button onClick={fetchRates} className="px-5 py-2.5 rounded-xl bg-[#0A2A4F] text-white text-sm font-bold hover:bg-[#0d3562]">Retry</button>
    </div>
  );

  return (
    <>
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">

        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex-1">
            <h2 className="text-base font-bold text-gray-900">Rate Management</h2>
            <p className="text-xs text-gray-400">All slabs for <span className="font-semibold text-gray-600">{serviceType}</span></p>
          </div>
          <input type="text" placeholder="Search user ID, name, mobile…" value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-gray-50 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0A2A4F] focus:bg-white transition w-full md:w-64" />
          <button onClick={() => { setSelectedUsers([]); fetchRates(); }}
            className="px-4 py-2.5 rounded-xl bg-gray-100 text-gray-600 text-sm font-bold hover:bg-gray-200 transition whitespace-nowrap">
            ↻ Refresh
          </button>
        </div>

        {/* Bulk action bar */}
        {selectedUsers.length > 0 && (
          <div className="px-5 py-3 bg-[#0A2A4F]/5 border-b border-[#0A2A4F]/10 flex flex-wrap items-center gap-3">
            <span className="text-sm font-bold text-[#0A2A4F]">{selectedUsers.length} user{selectedUsers.length > 1 ? "s" : ""} selected</span>
            <button onClick={() => onBulkEdit(selectedUsers, selectedUsers[0]?.slabs?.[0])}
              className="px-4 py-2 rounded-xl bg-[#0A2A4F] text-white text-xs font-bold hover:bg-[#0d3562] transition">
              Bulk Update ({selectedUsers.length})
            </button>
            <button onClick={() => setSelectedUsers([])}
              className="px-4 py-2 rounded-xl bg-white border border-gray-200 text-xs text-gray-600 font-medium hover:bg-gray-50">
              Clear
            </button>
          </div>
        )}

        {/* Sections */}
        <div className="p-4 space-y-3">
          {renderSection("MASTER_DISTRIBUTOR", byType("MASTER_DISTRIBUTOR"))}
          {renderSection("DISTRIBUTOR",        byType("DISTRIBUTOR"))}
          {renderSection("MERCHANT",           byType("MERCHANT"))}
        </div>
      </div>

      {/* ── Slab detail popup ── */}
      {popupUser && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-5xl rounded-3xl shadow-2xl max-h-[90vh] overflow-y-auto relative">
            <button onClick={() => setPopupUser(null)}
              className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 text-sm font-bold">
              ✕
            </button>

            <div className="p-6">
              {/* Popup header */}
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900">Rate Slabs</h2>
                <button
                  onClick={() => { onEditSlab({ serviceType, userType: popupUser.userType, userId: popupUser.userId, slab: null }); setPopupUser(null); }}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 transition"
                >
                  + Add New Slab
                </button>
              </div>

              {/* User chips */}
              <div className="flex flex-wrap gap-2 mb-5">
                {[["User ID", popupUser.userId], ["Name", popupUser.fullName], ["Mobile", popupUser.mobileNumber], ["Type", popupUser.userType], ["Shop", popupUser.shopName]]
                  .filter(([, v]) => v)
                  .map(([label, value]) => (
                    <div key={label} className="bg-gray-50 border border-gray-100 rounded-xl px-3 py-2">
                      <span className="text-gray-400 text-xs">{label}: </span>
                      <span className="font-bold text-gray-800 text-sm">{value}</span>
                    </div>
                  ))}
              </div>

              {/* Slabs table */}
              <div className="overflow-x-auto rounded-2xl border border-gray-100">
                <table className="w-full min-w-[860px] text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      {["Range","Agent Charge","Agent Comm","Dist Comm","MD Comm","Operator","Status","Actions"].map((h) => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-400 uppercase tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {popupUser.slabs.map((slab) => (
                      <tr key={slab.id} className="border-b border-gray-50 hover:bg-gray-50/60 transition">
                        <td className="px-4 py-3.5 font-bold text-gray-800 whitespace-nowrap">₹{slab.minAmount} – ₹{slab.maxAmount}</td>
                        <td className="px-4 py-3.5 text-orange-600 font-semibold">
                          {slab.agentChargeValue}
                          <span className="text-xs text-gray-400 ml-1">({slab.agentChargeType})</span>
                        </td>
                        <td className="px-4 py-3.5 text-emerald-600 font-semibold">
                          {slab.agentCommValue}
                          <span className="text-xs text-gray-400 ml-1">({slab.agentCommType})</span>
                        </td>
                        <td className="px-4 py-3.5 text-blue-600 font-semibold">
                          {slab.distCommValue}
                          <span className="text-xs text-gray-400 ml-1">({slab.distCommType})</span>
                        </td>
                        <td className="px-4 py-3.5 text-violet-600 font-semibold">
                          {slab.mdCommValue}
                          <span className="text-xs text-gray-400 ml-1">({slab.mdCommType})</span>
                        </td>
                        <td className="px-4 py-3.5 text-gray-400">{slab.operator || "—"}</td>
                        <td className="px-4 py-3.5">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${slab.isActive ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-500"}`}>
                            {slab.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex gap-2">
                            <button
                              onClick={() => { onEditSlab({ serviceType, userType: popupUser.userType, userId: popupUser.userId, slab }); setPopupUser(null); }}
                              className="px-3 py-1.5 rounded-lg bg-[#0A2A4F] text-white text-xs font-bold hover:bg-[#0d3562] transition">
                              Edit
                            </button>
                            <button
                              onClick={() => setConfirmSlab(slab)}
                              disabled={deletingSlabId === slab.id}
                              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${deletingSlabId === slab.id ? "bg-gray-100 text-gray-300 cursor-wait" : "bg-red-50 text-red-600 hover:bg-red-100"}`}>
                              {deletingSlabId === slab.id ? "…" : "Delete"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Slab delete confirm ── */}
      {confirmSlab && (
        <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center text-red-600 text-xl font-bold">!</div>
            <h3 className="text-lg font-bold text-gray-900 text-center mb-1">Delete Slab?</h3>
            <p className="text-sm text-gray-400 text-center mb-4">This will permanently remove the rate slab</p>
            <div className="bg-red-50 border border-red-100 rounded-xl py-3 px-4 mb-5 text-center">
              <p className="font-bold text-gray-900">₹{confirmSlab.minAmount} – ₹{confirmSlab.maxAmount}</p>
              <p className="text-xs text-red-500 mt-1 font-medium">Cannot be undone</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setConfirmSlab(null)}
                className="flex-1 py-3 rounded-xl bg-gray-100 text-gray-700 font-bold text-sm hover:bg-gray-200 transition">
                Cancel
              </button>
              <button onClick={() => handleDeleteSlab(confirmSlab)}
                className="flex-1 py-3 rounded-xl bg-red-600 text-white font-bold text-sm hover:bg-red-700 transition">
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ServiceRateListCard;
